import React, { useEffect, useState } from 'react';
import { RouteComponentProps, Redirect } from '@reach/router';
import { styled, useTheme } from '@material-ui/styles';
import { Card, Box, Chip, Theme } from '@material-ui/core';
import { injectIntl } from 'react-intl';
import { useQuery, gql } from '@apollo/client';
import { IStyledArguments } from '../../types/styled-arguments';
import Content from '../content';
import BackLink from '../link';

const GET_EXERCISE = gql`
  query GetExercise($id: ID!) {
    exercise(id: $id) {
      id
      name
      shortDescription
      taskDevelopment
      ageGroup
      duration
      objectives
      equipment
      coachingPoints
      variations
      images
      category
      subcategory
    }
  }
`;

const Main = styled('main')(({ theme }: IStyledArguments) => ({
  minHeight: `calc(100vh - ${theme.layout.headerHeight + theme.layout.tabsHeight}px )`,
  background: theme.palette.extra.backgroundGrey,
  paddingBottom: theme.layout.footerHeight,
}));

const DrillResultContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  maxWidth: 1200,
  margin: '32px auto',
  padding: theme.spacing(4),
  borderRadius: '18px',
  border: `1px solid ${theme.palette.extra.borderColor}`,
  background: theme.palette.background.paper,
  boxShadow: '0 1px 3px rgba(16, 21, 26, 0.06)',
}));

const DrillTitle = styled('h2')(({ theme }: IStyledArguments) => ({
  margin: '0 0 12px 0',
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '2.2rem',
  letterSpacing: '-0.5px',
}));

const SectionTitle = styled('h4')(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.primary.dark,
  fontSize: '1.15rem',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  letterSpacing: '0.5px',
}));

const DevelopmentStep = styled(Box)(({ theme }: IStyledArguments) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  paddingLeft: theme.spacing(2.5),
  padding: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: '12px',
  marginBottom: theme.spacing(2),
}));

const PageViewDrill = injectIntl<{ id: string } & RouteComponentProps>(({ id }) => {
  const theme = useTheme<Theme>();
  console.log('[PageViewDrill] Component rendered with id:', id);

  const { data, loading, error } = useQuery<any>(GET_EXERCISE, { variables: { id } });

  // Debug: log incoming id and raw response to help diagnose redirect
  console.log('[PageViewDrill] route id:', id);
  console.log('[PageViewDrill] query data:', data, 'loading:', loading, 'error:', error);
  
  const [drill, setDrill] = useState<any>(null);

  useEffect(() => {
    console.log('[PageViewDrill] useEffect: data changed', data);
    if (data && data.exercise) {
      console.log('[PageViewDrill] Setting drill:', data.exercise);
      setDrill(data.exercise);
    } else if (data && !data.exercise) {
      console.log('[PageViewDrill] data exists but exercise is null/undefined', data);
    }
  }, [data]);

  if (error) {
    console.log('[PageViewDrill] Rendering error UI:', error);
    return (
      <Main>
        <Content>
          <div style={{ padding: 16, color: 'red' }}>
            <strong>Error loading drill:</strong> {error.message}
            <pre style={{ fontSize: '0.85rem', overflow: 'auto' }}>{JSON.stringify(error, null, 2)}</pre>
          </div>
        </Content>
      </Main>
    );
  }

  // Show loading state while query is loading OR while waiting for useEffect to update state
  if (!drill) {
    if (loading || data === undefined) {
      console.log('[PageViewDrill] Rendering loading state (loading:', loading, 'data:', data, ')');
      return <Main><Content><div style={{ padding: '40px', textAlign: 'center' }}>Loading drill...</div></Content></Main>;
    }
    // Only redirect if data came back but exercise is null (not found)
    if (data && !data.exercise) {
      console.log('[PageViewDrill] Exercise not found, redirecting to /library/drills');
      return <Redirect to="/library/drills" noThrow />;
    }
    // Safety fallback: if we get here with no drill, show loading
    console.log('[PageViewDrill] Safety fallback: no drill and no clear reason, showing loading');
    return <Main><Content><div style={{ padding: '40px', textAlign: 'center' }}>Loading drill...</div></Content></Main>;
  }

  console.log('[PageViewDrill] Rendering drill page for:', drill?.name);
  return (
    <Main>
      <Content>
        <BackLink to="/library/drills">← Back to drills</BackLink>
        <DrillResultContainer>
          <DrillTitle>{drill?.name || 'Drill'}</DrillTitle>
          {drill?.shortDescription && (
            <p style={{ color: theme.palette.text.secondary, fontSize: '1.05rem', marginTop: '12px', marginBottom: '24px', lineHeight: 1.6 }}>
              {drill.shortDescription}
            </p>
          )}

          {/* Main Data */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 32, marginBottom: 32 }}>
            <div style={{ padding: '20px', background: theme.palette.extra.accentSoft, borderRadius: '14px', border: `1px solid ${theme.palette.extra.borderColor}` }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.palette.primary.dark, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Age Group</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: theme.palette.text.primary }}>{drill?.ageGroup || '—'}</div>
            </div>
            <div style={{ padding: '20px', background: theme.palette.extra.accentSoft, borderRadius: '14px', border: `1px solid ${theme.palette.extra.borderColor}` }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.palette.primary.dark, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Duration</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: theme.palette.text.primary }}>{drill?.duration ? `${drill.duration} min` : '—'}</div>
            </div>
          </div>

          {/* Objectives */}
          {(Array.isArray(drill?.objectives) && drill.objectives.length > 0) && (
            <div style={{ marginBottom: 32 }}>
              <SectionTitle>Objectives</SectionTitle>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {(drill?.objectives || []).map((o: string, i: number) => (
                  <li key={i} style={{
                    padding: '12px 16px',
                    background: theme.palette.extra.backgroundGrey,
                    border: `1px solid ${theme.palette.extra.borderColor}`,
                    borderRadius: '12px',
                    color: theme.palette.text.primary,
                    fontSize: '0.95rem',
                    lineHeight: 1.5
                  }}>
                    • {o}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Equipment */}
          {(Array.isArray(drill?.equipment) && drill.equipment.length > 0) && (
            <div style={{ marginBottom: 32 }}>
              <SectionTitle>Equipment</SectionTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {(drill?.equipment || []).map((e: string, i: number) => (
                  <div key={i} style={{
                    padding: '10px 16px',
                    background: theme.palette.extra.accentSoft,
                    border: `1px solid ${theme.palette.extra.borderColor}`,
                    borderRadius: '14px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: theme.palette.primary.dark,
                  }}>
                    {e}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Task Development */}
          {(Array.isArray(drill?.taskDevelopment) && drill.taskDevelopment.length > 0) ? (
            <div style={{ marginBottom: 32 }}>
              <SectionTitle>Task Development</SectionTitle>
              {(drill?.taskDevelopment as string[]).map((step: string, idx: number) => (
                <DevelopmentStep key={idx}>
                  <div style={{ fontWeight: 700, color: theme.palette.primary.dark, marginBottom: '8px' }}>Step {idx + 1}</div>
                  <p style={{ margin: 0, color: theme.palette.text.primary, lineHeight: 1.6, fontSize: '0.95rem' }}>{step}</p>
                </DevelopmentStep>
              ))}
            </div>
          ) : null}

          {/* Coaching Points */}
          {(Array.isArray(drill?.coachingPoints) && drill.coachingPoints.length > 0) && (
            <div style={{ marginBottom: 32 }}>
              <SectionTitle>Coaching Points</SectionTitle>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(drill?.coachingPoints || []).map((c: string, i: number) => (
                  <li key={i} style={{ 
                    padding: '12px 16px',
                    marginBottom: '8px',
                    background: theme.palette.extra.backgroundGrey,
                    border: `1px solid ${theme.palette.extra.borderColor}`,
                    borderRadius: '12px',
                    color: theme.palette.text.primary,
                    fontSize: '0.95rem',
                    lineHeight: 1.5
                  }}>
                    → {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Variations */}
          {(Array.isArray(drill?.variations) && drill.variations.length > 0) && (
            <div>
              <SectionTitle>Variations</SectionTitle>
              {(drill?.variations || []).map((v: string, i: number) => (
                <DevelopmentStep key={i}>
                  <div style={{ fontWeight: 700, color: theme.palette.primary.dark, marginBottom: '8px' }}>Variation {i + 1}</div>
                  <p style={{ margin: 0, color: theme.palette.text.primary, lineHeight: 1.6, fontSize: '0.95rem' }}>{v}</p>
                </DevelopmentStep>
              ))}
            </div>
          )}
        </DrillResultContainer>
      </Content>
    </Main>
  );
});

export default PageViewDrill;
